import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
/** Components */
import { PageHelper } from 'Components/PageHelper';
import { Vinculos } from './Bonds';
import { Container } from '../../../Components/Container';
import { TitleForm } from 'Components/Form/styles';
import { FormSuccess } from 'Components/Form/Success';
import { Row } from 'Components/Grid';
import { StepperForm } from 'Components/StepperForm';
import { Detalhes } from './Details';
/** Services */
import { addAsset, editAsset, getAssetById } from 'services/assets-service';
/** State Managers */
import { useAuthTenant } from 'store/auth';
import { useSelector } from 'react-redux';
/** Third Lib Modal */
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { useQuery } from 'react-query';
import { PageLoaderForm } from 'Components/PageLoader/Form';

export const AssetsForm = () => {
  const { t } = useTranslation(['asm']);
  const steps = [t('asm:assets.form.detailsStep'), t('asm:assets.form.bondsStep')];
  const selectedTenant = useSelector(useAuthTenant);
  const { id } = useParams();
  const SwalReact = withReactContent(Swal);
  const navigate = useNavigate();
  const [idAsset, setIdAsset] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [finished, setFinished] = useState(false);
  const [isLoadingForm, setIsLoadingForm] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      name: '',
      criticity: '',
      type: '',
      dataProcessing: '',
      location: '',
      pii: false,
      user: '',
      description: '',
      tags: [],
      areas: [],
      processes: [],
      systems: [],
      companyKey: null,
    },
  });

  const totalSteps = () => {
    return steps.length;
  };

  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  const handleNext = () => {
    const newActiveStep = isLastStep() ? activeStep : activeStep + 1;
    setActiveStep(newActiveStep);
  };

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1);
  };

  const handleComplete = async () => {
    try {
      setIsLoadingForm(true);
      const assetFormmated = formatForm(getValues());
      const res = id ? await editAsset(id, assetFormmated) : await addAsset(assetFormmated);
      if (res.status === 200 || res.status === 201) {
        setIdAsset(res.data._id ?? id);
        setFinished(true);
        setIsLoadingForm(false);
      } else {
        throw new Error('Ocorreu um erro ao salvar/editar um Ativo!');
      }
    } catch (error) {
      setIsLoadingForm(false);
      SwalReact.fire({
        title: t('asm:assets.delete.errorTitle'),
        text: error.response.data.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const formatProcess = process => {
    return {
      _id: process._id,
      name: process.name,
      description: process.description,
      user: process.user,
      criticity: process.criticity,
    };
  };

  const formatArea = area => {
    return {
      _id: area._id,
      name: area.name,
      description: area.description,
      user: area.user,
      criticity: area.criticity,
    };
  };

  const formatSystem = system => {
    return {
      _id: system._id,
      name: system.name,
      description: system.description,
      criticity: system.criticity,
      user: system.user,
    };
  };

  const formatForm = dados => {
    return {
      name: dados.name,
      criticity: dados.criticity,
      type: dados.type,
      user: dados.user,
      tags: dados.tags.length > 0 ? dados.tags : [],
      pii: dados.pii,
      location: dados.location,
      description: dados.description,
      companyId: dados.user.companyId,
      areas: dados.areas.length > 0 ? dados.areas.map(area => formatArea(area)) : [],
      processes: dados.processes.length > 0 ? dados.processes.map(process => formatProcess(process)) : [],
      systems: dados.systems.length > 0 ? dados.systems.map(system => formatSystem(system)) : [],
      companyKey: dados.companyKey ?? selectedTenant.slug,
    };
  };

  const handleReset = () => {
    reset();
    setActiveStep(0);
    setFinished(false);
    setIdAsset(null);
    navigate('/assets/add');
  };

  const getTitleStep = () => {
    if (finished) return <></>;

    if (activeStep === 0)
      return (
        <Row>
          <TitleForm>{t('asm:assets.form.detailsStep')}</TitleForm>
        </Row>
      );

    return (
      <Row>
        <TitleForm>{t('asm:assets.form.bondsStep')}</TitleForm>
      </Row>
    );
  };

  const {
    isLoading,
    error,
    data: asset,
  } = useQuery(['getAsset', id], async () => {
    if (!id) return null;
    const resAsset = await getAssetById(id);

    if (!resAsset.data) return null;

    return resAsset.data;
  });

  useEffect(() => {
    if (!id) return;
    if (!asset) return;

    Object.keys(asset).forEach(key => {
      setValue(key, asset[key]);
    });

    return () => {};
  }, [id, asset, setValue]);

  const showForm = () => {
    if (id) return !isLoading && !error;
    return true;
  };

  return (
    <Container title={id ? t('asm:assets.form.editTitle') : t('asm:assets.form.addTitle')} breadcrumb={t('asm:assets.form.breadcrumb')} linkPage="/assets" data-tour="page-title">
      {isLoading && <PageLoaderForm />}
      {showForm() && (
        <>
          <div data-tour="stepper" style={{ width: '100%' }}>
          <StepperForm activeStep={activeStep} steps={steps} />
          </div>
          <div className="card-content">
            {getTitleStep()}
            {activeStep === 0 && (
              <Detalhes
                control={control}
                register={register}
                handleNext={handleNext}
                handleSubmit={handleSubmit}
                errors={errors}
                getValues={getValues}
                id={id}
              />
            )}
            {activeStep === 1 && !finished && (
              <Vinculos
                control={control}
                register={register}
                handleBack={handleBack}
                handleComplete={handleComplete}
                handleSubmit={handleSubmit}
                isLoading={isLoadingForm}
              />
            )}
            {finished && (
              <FormSuccess
                title={t('asm:assets.form.success')}
                labelStartAgain={t('asm:assets.form.addAnother')}
                handleStartAgain={() => handleReset()}
                labelShow={t('asm:assets.form.viewDetails')}
                handleShow={() => navigate('/assets/show/' + idAsset)}
              />
            )}
          </div>
        </>
      )}
      {!finished && <PageHelper steps={tourSteps} tourKey="assets-form" />}
    </Container>
  );
};

const tourSteps = [
  {
    target: 'body',
    content: 'Bem-vindo ao formulário de cadastro de Ativos! Aqui você pode adicionar ou editar os recursos que suportam suas operações.',
    disableBeacon: true,
    disableScrolling: true,
    placement: 'center',
  },
  {
    target: '[data-tour="page-title"]',
    content: 'Clique no título para voltar à lista de ativos, caso necessário.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="stepper"]',
    content: 'O cadastro é dividido em 2 etapas: Detalhes (informações básicas) e Vínculos (associações com áreas, processos e sistemas).',
    disableScrolling: true,
  },
  {
    target: '[data-tour="asset-name"]',
    content: 'Digite o nome do ativo. Este campo é obrigatório e não pode ser alterado após a criação.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="asset-criticity"]',
    content: 'Selecione a criticidade do ativo. A escala vai de 1 (Muito Baixa - verde) a 5 (Muito Alta - vermelho). Este indicador ajuda a priorizar a gestão.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="asset-type"]',
    content: 'Escolha o tipo do ativo: Pessoa (colaboradores, gestores), Tecnologia (computadores, servidores) ou Ambiente (datacenters, escritórios).',
    disableScrolling: false,
  },
  {
    target: '[data-tour="asset-user"]',
    content: 'Selecione o responsável pelo ativo. Este campo é obrigatório e define quem gerencia este recurso.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="asset-tags"]',
    content: 'Adicione tags para categorizar o ativo. Digite e pressione Enter para adicionar cada tag. Útil para buscas e filtros.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="asset-location"]',
    content: 'Informe a localização física ou lógica do ativo (ex: "Escritório São Paulo", "Servidor AWS us-east-1").',
    disableScrolling: false,
  },
  {
    target: '[data-tour="asset-pii"]',
    content: 'Marque se o ativo processa PII (Personally Identifiable Information) - dados pessoais que podem identificar um indivíduo.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="asset-description"]',
    content: 'Descreva o ativo em detalhes. Este campo é obrigatório e ajuda outros usuários a entenderem o propósito e função do recurso.',
    disableScrolling: false,
  },
  {
    target: '[data-tour="form-actions"]',
    content: 'Use "Cancelar" para voltar sem salvar ou "Avançar" para ir à próxima etapa de vínculos.',
    disableScrolling: false,
  },
  {
    target: 'body',
    content: 'Tour concluído! Preencha todas as informações necessárias. Na próxima etapa, você poderá vincular o ativo a áreas, processos e sistemas.',
    disableScrolling: true,
    placement: 'center',
  },
];
